import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, ArrowLeft, Calendar, Clock, Check, Loader2, Send, Video } from "lucide-react";
import { api, apiError } from "../lib/api";
import { BrandMark } from "./BrandMark";

// The prospect's browser timezone (e.g. "Australia/Perth", "America/Los_Angeles").
// Falls back to AEDT if the browser doesn't cooperate. Determined ONCE per
// modal mount so we don't jitter as slots re-render.
function browserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Sydney"; }
  catch { return "Australia/Sydney"; }
}

// Format a UTC ISO string as a `9:00 am` label in the given timezone.
function timeInTz(iso, tz) {
  try {
    return new Intl.DateTimeFormat("en-AU", {
      hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz,
    }).format(new Date(iso)).toLowerCase();
  } catch { return ""; }
}

function dayInTz(iso, tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-AU", {
      weekday: "short", day: "numeric", month: "short", timeZone: tz,
    }).formatToParts(new Date(iso));
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    return { dow: get("weekday"), dnum: get("day"), mon: get("month") };
  } catch { return { dow: "", dnum: "", mon: "" }; }
}

const dayKeyOf = (p) => `${p.dow} ${p.dnum} ${p.mon}`;

// An AEDT weekday can straddle two local calendar days overseas (e.g. Mon 9am
// AEDT = Sun 11pm UTC). Label the tile by the local day most slots fall on.
function dayHeaderInTz(slots, tz) {
  const counts = new Map();
  for (const s of slots) {
    const p = dayInTz(s.iso, tz);
    const k = dayKeyOf(p);
    counts.set(k, { p, n: (counts.get(k)?.n || 0) + 1 });
  }
  let best = { p: { dow: "", dnum: "", mon: "" }, n: 0 };
  for (const v of counts.values()) if (v.n > best.n) best = v;
  return best.p;
}

/**
 * BookingModal — the only CTA on the marketing site.
 *
 * Flow: Pick a day → pick a time slot → drop contact info → done.
 *
 * Design notes:
 *  - Ink + lime, glass grain overlay, big serif headline, tight editorial
 *    subhead. Aims to feel like Superhuman × Linear × a boutique concierge
 *    service — not a generic Calendly embed.
 *  - Locks page scroll while open (fixed inset-0 + preventDefault on wheel).
 *  - Two-step wizard so the merchant only sees the fields they need at
 *    each step; a slim step tracker up top keeps the momentum.
 */
export function BookingModal({ open, onClose }) {
  const [step, setStep] = useState("slot"); // slot | contact | done
  const [days, setDays] = useState([]);
  const [tz, setTz] = useState("Australia/Sydney");
  const [localTz] = useState(browserTz); // browser's own timezone, captured once
  const localTzShort = useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-AU", { timeZoneName: "short", timeZone: localTz }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || localTz;
    } catch { return localTz; }
  }, [localTz]);
  const showLocal = localTz !== tz; // if the prospect is in AEDT, no need to dual-display
  const [pickedDay, setPickedDay] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmedLabel, setConfirmedLabel] = useState(null);
  const [confirmedIso, setConfirmedIso] = useState(null);
  const [manageUrl, setManageUrl] = useState(null);
  const [zoomUrl, setZoomUrl] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", notes: "" });

  // Fetch availability the moment the modal opens (never before — the request
  // is public but this keeps the marketing page from firing a call on load).
  useEffect(() => {
    if (!open) return;
    // Reset state each time it re-opens so a second visit feels fresh.
    setStep("slot"); setPickedDay(null); setPickedSlot(null); setConfirmedLabel(null); setConfirmedIso(null); setManageUrl(null); setZoomUrl(null);
    setForm({ name: "", email: "", phone: "", business: "", notes: "" });
    api.get("/bookings/slots").then((r) => {
      const ds = r.data?.days || [];
      setDays(ds);
      setTz(r.data?.timezone || "Australia/Sydney");
      // Auto-select the first day that has any open slot so the user sees
      // real times without a click.
      const firstOpenDay = ds.find((d) => d.slots.some((s) => !s.booked));
      if (firstOpenDay) setPickedDay(firstOpenDay.day_key);
    }).catch(() => setDays([]));
  }, [open]);

  const dayObj = useMemo(() => days.find((d) => d.day_key === pickedDay), [days, pickedDay]);

  const submit = async () => {
    if (!pickedSlot) return;
    if (!form.name || !form.email || !form.business) {
      toast.error("Add your name, email and business. We'll do the rest.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { slot_id: pickedSlot, ...form });
      setConfirmedLabel(data.slot_label);
      setConfirmedIso(data.slot_iso || null);
      setZoomUrl(data.zoom_url || null);
      setManageUrl(data.manage_url || null);
      setStep("done");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#0B0D10]/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="booking-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[880px] max-h-[92vh] bg-[#F4F1E9] rounded-[26px] overflow-hidden shadow-[0_40px_120px_-20px_rgba(0,0,0,0.55)] flex flex-col"
        >
          {/* Header — ink strip with brand + step tracker + close */}
          <div className="relative bg-[#0B0D10] text-white px-6 sm:px-8 py-5 flex items-center justify-between overflow-hidden shrink-0">
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#C8FF4D]/12 blur-3xl pointer-events-none" />
            <div className="relative z-10 flex items-center gap-3">
              <BrandMark size={28} tone="light" />
              <div>
                <div className="font-display font-extrabold text-[17px] tracking-tight">BackPocket</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#C8FF4D]">Book a 15-min call</div>
              </div>
            </div>
            <button data-testid="booking-close" onClick={onClose} className="relative z-10 h-9 w-9 rounded-full grid place-items-center bg-white/[0.08] hover:bg-white/[0.15] text-white transition-colors">
              <X size={16} />
            </button>
          </div>

          {/* Body — scrolls internally */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-9">
            <AnimatePresence mode="wait">
              {step === "slot" && (
                <motion.div key="slot" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>
                  <div className="max-w-lg">
                    <h2 className="font-display font-extrabold tracking-[-0.02em] leading-[1.02] text-[clamp(1.8rem,3.4vw,2.4rem)]">
                      Pick a time. <span className="font-serif-editorial italic font-normal tracking-normal text-[#3a3d44]/80">We do the rest.</span>
                    </h2>
                    <p className="mt-3 text-[14px] text-[#3a3d44] leading-relaxed">
                      A 15-minute video call to choose your rewards and show you exactly what we'll build.{showLocal ? ` Times in your local time (${localTzShort}).` : ""}
                    </p>
                  </div>

                  {/* Day picker — horizontal scroll of ~14 weekdays */}
                  <div className="mt-8 -mx-6 sm:-mx-9 px-6 sm:px-9 overflow-x-auto no-scrollbar">
                    <div className="flex gap-2 pb-2 min-w-max" data-testid="booking-days">
                      {days.map((d) => {
                        const anyOpen = d.slots.some((s) => !s.booked);
                        const on = d.day_key === pickedDay;
                        const local = dayHeaderInTz(d.slots, localTz);
                        const dow = showLocal ? local.dow : d.day_label.split(" ")[0];
                        const dnum = showLocal ? local.dnum : d.day_label.split(" ")[1];
                        const mon = showLocal ? local.mon : d.day_label.split(" ")[2];
                        return (
                          <button
                            key={d.day_key}
                            data-testid={`booking-day-${d.day_key}`}
                            disabled={!anyOpen}
                            onClick={() => { setPickedDay(d.day_key); setPickedSlot(null); }}
                            className={`shrink-0 w-[74px] rounded-2xl border transition-all py-3 text-center ${
                              on
                                ? "bg-[#0B0D10] border-[#0B0D10] text-white"
                                : anyOpen
                                  ? "bg-white border-[#E4E0D6] hover:border-[#0B0D10]/40 text-[#0B0D10]"
                                  : "bg-white/40 border-[#E4E0D6] text-[#3a3d44]/30 cursor-not-allowed"
                            }`}
                          >
                            <div className={`text-[11px] uppercase tracking-[0.14em] ${on ? "text-[#C8FF4D]" : "text-[#3a3d44]/60"}`}>{dow}</div>
                            <div className="font-display font-extrabold tracking-[-0.02em] text-[22px] leading-none mt-1">{dnum}</div>
                            <div className={`text-[11px] mt-1 ${on ? "text-white/60" : "text-[#3a3d44]/55"}`}>{mon}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time slot grid for the picked day */}
                  <div className="mt-7">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[#3a3d44]/55 mb-3 flex items-center gap-1.5">
                      <Clock size={11} strokeWidth={2.2} /> {dayObj ? (showLocal ? `${dayKeyOf(dayHeaderInTz(dayObj.slots, localTz))} · your time` : dayObj.day_label) : "Choose a day"}
                    </div>
                    {dayObj ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="booking-slots">
                        {dayObj.slots.map((s) => {
                          const on = s.id === pickedSlot;
                          const headerKey = showLocal ? dayKeyOf(dayHeaderInTz(dayObj.slots, localTz)) : null;
                          const slotDay = showLocal ? dayInTz(s.iso, localTz) : null;
                          const otherDay = showLocal && dayKeyOf(slotDay) !== headerKey ? slotDay.dow : "";
                          const label = showLocal ? timeInTz(s.iso, localTz) : s.label;
                          return (
                            <button
                              key={s.id}
                              data-testid={`booking-slot-${s.id}`}
                              disabled={s.booked}
                              onClick={() => setPickedSlot(s.id)}
                              title={showLocal ? `${label} (${s.label} AEDT)` : label}
                              className={`h-11 rounded-full text-[13.5px] font-medium tabular-nums transition-all ${
                                s.booked
                                  ? "bg-white/40 text-[#3a3d44]/30 line-through cursor-not-allowed"
                                  : on
                                    ? "bg-[#C8FF4D] text-[#0B0D10] shadow-[0_8px_18px_-6px_rgba(200,255,77,0.75)]"
                                    : "bg-white border border-[#E4E0D6] text-[#0B0D10] hover:border-[#0B0D10]/40"
                              }`}
                            >{label}{otherDay && <span className="ml-1 text-[10px] opacity-60">{otherDay}</span>}</button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-2xl border-2 border-dashed border-[#E4E0D6] p-8 text-center text-[13px] text-[#3a3d44]/55">
                        Pick a day above to see available times.
                      </div>
                    )}
                  </div>

                  {/* Continue */}
                  <div className="mt-9 flex items-center gap-3 flex-wrap">
                    <button
                      data-testid="booking-slot-continue"
                      disabled={!pickedSlot}
                      onClick={() => setStep("contact")}
                      className="btn-lift bg-[#0B0D10] text-white rounded-full h-12 px-6 font-semibold text-[14.5px] disabled:opacity-40 flex items-center gap-2"
                    >
                      Continue <ArrowLeft size={14} className="rotate-180" />
                    </button>
                    {pickedSlot && (
                      <span className="text-[13px] text-[#3a3d44]/70">
                        Selected: <span className="font-medium text-[#0B0D10]">
                          {(() => {
                            const s = dayObj?.slots.find((x) => x.id === pickedSlot);
                            if (!s) return "";
                            const local = showLocal ? timeInTz(s.iso, localTz) : "";
                            return showLocal ? `${dayInTz(s.iso, localTz).dow} ${dayInTz(s.iso, localTz).dnum} ${dayInTz(s.iso, localTz).mon}, ${local} · ${s.label} AEDT`
                                             : `${dayObj?.day_label}, ${s.label}`;
                          })()}
                        </span>
                      </span>
                    )}
                  </div>
                </motion.div>
              )}

              {step === "contact" && (
                <motion.div key="contact" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>
                  <button onClick={() => setStep("slot")} className="text-[13px] text-[#3a3d44]/70 hover:text-[#0B0D10] inline-flex items-center gap-1.5 mb-5" data-testid="booking-back-to-slot">
                    <ArrowLeft size={13} /> Change time
                  </button>
                  <div className="rounded-2xl bg-[#0B0D10] text-white p-4 mb-6 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#C8FF4D] text-[#0B0D10] grid place-items-center shrink-0"><Calendar size={16} strokeWidth={2.4} /></div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-[#C8FF4D]">Your slot</div>
                      {(() => {
                        const s = dayObj?.slots.find((x) => x.id === pickedSlot);
                        if (!s) return null;
                        const localLine = showLocal ? `${dayInTz(s.iso, localTz).dow} ${dayInTz(s.iso, localTz).dnum} ${dayInTz(s.iso, localTz).mon}, ${timeInTz(s.iso, localTz)}` : null;
                        return (
                          <>
                            <div className="font-display font-bold text-[15px] tracking-[-0.01em] mt-0.5">{localLine || `${dayObj?.day_label}, ${s.label} AEDT`}</div>
                            {localLine && <div className="text-[11px] text-white/50 mt-0.5">{dayObj?.day_label}, {s.label} AEDT</div>}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="max-w-lg">
                    <h2 className="font-display font-extrabold tracking-[-0.02em] leading-[1.02] text-[clamp(1.6rem,2.8vw,2.1rem)]">Almost there.</h2>
                    <p className="mt-2 text-[14px] text-[#3a3d44] leading-relaxed">Where should we send your confirmation?</p>
                  </div>

                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jamie Smith" testid="booking-name" />
                    <Field label="Business name" value={form.business} onChange={(v) => setForm({ ...form, business: v })} placeholder="Ora Coffee" testid="booking-business" />
                    <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jamie@oracoffee.com.au" testid="booking-email" />
                    <Field label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+61 412 345 678" testid="booking-phone" optional />
                  </div>
                  <label className="block mt-4">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/60 font-medium">Anything we should know? <span className="normal-case tracking-normal text-[#3a3d44]/50 font-normal">Optional</span></span>
                    <textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={3} placeholder="How many regulars? What kind of rewards? What POS?"
                      className="w-full mt-1.5 rounded-xl bg-white border border-[#E4E0D6] focus:border-[#0B0D10] focus:outline-none px-3.5 py-2.5 text-[14px] resize-y" />
                  </label>

                  <div className="mt-7 flex items-center gap-3 flex-wrap">
                    <button
                      data-testid="booking-submit"
                      disabled={busy}
                      onClick={submit}
                      className="btn-lift bg-[#C8FF4D] text-[#0B0D10] rounded-full h-12 px-7 font-semibold text-[14.5px] disabled:opacity-60 flex items-center gap-2 shadow-[0_10px_28px_-8px_rgba(200,255,77,0.7)]"
                    >
                      {busy ? <><Loader2 size={14} className="animate-spin" />Booking you in…</> : <><Send size={14} />Book this call</>}
                    </button>
                    <span className="text-[12px] text-[#3a3d44]/55">No obligation.</span>
                  </div>
                </motion.div>
              )}

              {step === "done" && (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.34, ease: [0.22, 0.61, 0.36, 1] }}
                  className="text-center py-6" data-testid="booking-done">
                  <div className="mx-auto h-14 w-14 rounded-full bg-[#C8FF4D] grid place-items-center">
                    <Check size={26} className="text-[#0B0D10]" strokeWidth={3} />
                  </div>
                  <h2 className="font-display font-extrabold tracking-[-0.02em] leading-[1.02] text-[clamp(1.8rem,3vw,2.4rem)] mt-6">You're booked.</h2>
                  {(() => {
                    const local = showLocal && confirmedIso ? `${dayInTz(confirmedIso, localTz).dow} ${dayInTz(confirmedIso, localTz).dnum} ${dayInTz(confirmedIso, localTz).mon}, ${timeInTz(confirmedIso, localTz)}` : null;
                    return (
                      <p className="mt-3 text-[15px] text-[#3a3d44] max-w-md mx-auto leading-relaxed">
                        Locked in for <span className="font-medium text-[#0B0D10]">{local || confirmedLabel}</span>.
                        {local && <span className="block text-[12.5px] text-[#3a3d44]/60 mt-1">({confirmedLabel})</span>}
                        <span className="block mt-2">Confirmation and video link are in your inbox.</span>
                      </p>
                    );
                  })()}
                  {zoomUrl && (
                    <a href={zoomUrl} target="_blank" rel="noreferrer" data-testid="booking-zoom-link"
                      className="btn-lift mt-6 inline-flex items-center gap-2 bg-[#C8FF4D] text-[#0B0D10] rounded-full h-11 px-5 font-semibold text-[13.5px] shadow-[0_8px_20px_-6px_rgba(200,255,77,0.7)]">
                      <Video size={14} strokeWidth={2.4} /> Join Zoom
                    </a>
                  )}
                  {manageUrl && (
                    <div className="mt-6 mx-auto max-w-md rounded-2xl border border-[#E4E0D6] bg-white p-3.5 text-left" data-testid="booking-manage-link">
                      <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#3a3d44]/55 font-medium mb-1">Change your booking</div>
                      <div className="flex items-center gap-2">
                        <a href={manageUrl} target="_blank" rel="noreferrer" className="flex-1 min-w-0 text-[12.5px] font-mono bg-transparent text-[#0B0D10]/85 truncate hover:opacity-70" data-testid="booking-manage-link-anchor">{manageUrl}</a>
                        <button onClick={() => { navigator.clipboard.writeText(manageUrl).then(() => toast.success("Link copied"), () => {}); }} className="text-[11.5px] font-semibold text-[#0B0D10] hover:opacity-70">Copy</button>
                      </div>
                    </div>
                  )}
                  <button onClick={onClose} data-testid="booking-done-close" className="btn-lift mt-9 bg-[#0B0D10] text-white rounded-full h-11 px-6 font-semibold text-[14px]">
                    Close
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, optional }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/60 font-medium">
        {label} {optional && <span className="normal-case tracking-normal text-[#3a3d44]/50 font-normal">Optional</span>}
      </span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1.5 rounded-xl bg-white border border-[#E4E0D6] focus:border-[#0B0D10] focus:outline-none px-3.5 h-11 text-[14px]"
      />
    </label>
  );
}
